import React, { useEffect } from 'react';
import { Grid, GridCol, GridRow } from '@naf/grid';
import { useDispatch } from 'react-redux';
import { useAuth0Token } from '@hooks/useAuth0Token';
import useSelector from '@redux/typedHooks';
import { actions as membershipActions } from '@redux/modules/myMembership';
import { LayoutWithMainContent } from '@components/layout/Layout';
import { BenefitsType } from '@appTypes/benefitsType';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import SchemaArticle from '@components/structured-data/SchemaArticle/SchemaArticle';
import { useDocumentUrl } from '@hooks/Documents/useDocumentUrl';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { useBecomeAMemberHandler } from '@utils/Tracking/Button/useBecomeAMemberHandler';
import { useCtaBlankExternalTracking } from '@utils/Tracking/Button/useCtaBlankExternalTracking';
import { DEFAULT_IMG_URL } from '@lib/constants';
import AppAnnouncement from './AppAnnouncement/AppAnnouncement';
import CampaignBannerMapper from './CampaignBannerMapper/CampaignBannerMapper';
import { BenefitImage } from './BenefitImage/BenefitImage';
import NewRelatedBenefits from './NewRelatedBenefits/NewRelatedBenefits';
import { BenefitLayout } from './BenefitLayout/BenefitLayout';
import { BenefitArticleLeftColumn } from './BenefitArticleLeftColumn/BenefitArticleLeftColumn';
import { BenefitArticleRightColumn } from './BenefitArticleRightColumn/BenefitArticleRightColumn';
import * as S from './BenefitArticle.styles';

interface Props {
  benefitArticle: BenefitsType;
}

export const BenefitArticle = ({ benefitArticle }: Props) => {
  const windowPath = useWindowLocation();
  const { simpleToken } = useAuth0Token();
  const dispatch = useDispatch();
  const categoriesState = useSelector((state) => state.sitestructure);
  const {
    meta: { fetchState },
  } = useSelector((state) => state.myMembership.customerInformation);

  const {
    slug,
    title,
    ingress,
    body,
    id,
    video,
    image,
    campaign,
    tips,
    relatedContent,
    meta,
    seoConfig,
    partner,
    category,
    keyInformation,
    callToAction,
    metaDisplay,
    urlParameters,
    displayNafApp,
    stepByStepSection,
    discountCodes,
    accessDetails,
    preview,
    isFavorite,
  } = benefitArticle || {};
  const url = useDocumentUrl(slug);
  const onClickBecomeAMember = useBecomeAMemberHandler();
  const handleCtaBlankExternalTracking = useCtaBlankExternalTracking();
  const { getProcessedImage } = useCloudinary();

  useEffect(() => {
    if (simpleToken && fetchState === 'initial') {
      dispatch(membershipActions.getCustomerInformation.request(simpleToken));
    }
  }, [fetchState, dispatch, simpleToken]);

  if (!id) return null;

  const seoDescription = seoConfig?.introduction ? seoConfig.introduction : ingress;
  const seoTitle = seoConfig?.title ? seoConfig.title : title;
  const mappedCategory = category?.slug ? categoriesState?.mappedCategories[category.slug].data : null;
  const schemaImage = getProcessedImage(image?.publicId || image?.public_id, {
    height: 630,
    width: 1200,
    focusMethod: image?.focus,
  })?.toURL();

  const mappedTips = tips?.map((t) => ({ ...t, color: 'yellow20' as const, type: 'tips' })) || [];
  const mappedCampaigns = campaign?.map((c) => ({ ...c, type: 'campaign' })) || [];
  const desktopCampaignAndTipsView = [...mappedCampaigns, ...mappedTips];
  const showCampaignsInDesktopView = desktopCampaignAndTipsView.length >= 3;

  return (
    <LayoutWithMainContent
      title={title}
      description={ingress || seoDescription}
      url={windowPath}
      imgUrl={schemaImage || DEFAULT_IMG_URL}
      gtmArgs={{
        page_type: 'BenefitPage',
        pageCategory: `${mappedCategory?.prettyUrl}/${title}`,
        contentId: id,
      }}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch || preview}
      preview={preview}
      seoTitle={seoTitle}
      seoDescription={seoDescription}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Benefit,
        imagePublicId: image?.publicId,
        partnerName: partner?.partnerName,
        partnerLogoPublicId: partner?.logo?.publicId,
      }}
      isMemberBenefitPage
    >
      <S.BenefitWrapper>
        <SchemaArticle
          title={title}
          description={ingress}
          imgUrl={schemaImage}
          datePublished={meta?.publishAt}
          dateModified={meta?.updatedAt}
          url={url}
          id={slug}
        />
        <Grid>
          <GridRow>
            <GridCol s="12" m="12" l="12" xl="12">
              <S.BenefitContent>
                <BreadCrumb />
              </S.BenefitContent>
              <BenefitImage image={image} video={video} partner={partner} />
              <S.BenefitContent>
                <BenefitLayout
                  left={
                    <BenefitArticleLeftColumn
                      id={id}
                      slug={slug}
                      title={title}
                      ingress={ingress}
                      keyInformation={keyInformation}
                      isFavorite={isFavorite}
                      desktopCampaignAndTipsView={desktopCampaignAndTipsView}
                      stepByStepSection={stepByStepSection}
                      accessDetails={accessDetails}
                      discountCodes={discountCodes}
                      callToAction={callToAction}
                      urlParameters={urlParameters}
                      onClickBecomeAMember={onClickBecomeAMember}
                      handleCtaBlankExternalTracking={handleCtaBlankExternalTracking}
                      body={body}
                      meta={meta}
                      metaDisplay={metaDisplay}
                    />
                  }
                  right={
                    <BenefitArticleRightColumn
                      id={id}
                      slug={slug}
                      keyInformation={keyInformation}
                      isFavorite={isFavorite}
                      desktopCampaignAndTipsView={desktopCampaignAndTipsView}
                      showCampaignsInDesktopView={showCampaignsInDesktopView}
                      handleCtaBlankExternalTracking={handleCtaBlankExternalTracking}
                    />
                  }
                />
                {showCampaignsInDesktopView && desktopCampaignAndTipsView.length > 0 && (
                  <GridRow>
                    <GridCol s="12" m="12" l="12" xl="12">
                      <S.DesktopWrapper>
                        <S.CampaignDiscountWrapper>
                          <CampaignBannerMapper
                            campaigns={desktopCampaignAndTipsView}
                            handleCtaBlankExternalTracking={handleCtaBlankExternalTracking}
                          />
                        </S.CampaignDiscountWrapper>
                      </S.DesktopWrapper>
                    </GridCol>
                  </GridRow>
                )}
                {displayNafApp?.nafApp && <AppAnnouncement />}
                {relatedContent && relatedContent.length > 0 && <NewRelatedBenefits relatedContent={relatedContent} />}
                {!metaDisplay?.hideForm && (
                  <S.StyledGridRow>
                    <GridCol s="12" m="12" l="8" xl="8">
                      <FeedbackSurvey contentUrl={windowPath} />
                    </GridCol>
                  </S.StyledGridRow>
                )}
              </S.BenefitContent>
            </GridCol>
          </GridRow>
        </Grid>
      </S.BenefitWrapper>
    </LayoutWithMainContent>
  );
};

export default BenefitArticle;
